import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  LinearProgress,
  Link,
} from '@mui/material'
import { formatTime } from '../../utils/dateUtils'

interface Lead {
  id: string
  fullName: string
  phone: string
  status: 'new' | 'qualified' | 'contacted' | 'booking_requested'
  source: string
  qualificationScore?: number
  createdAt: Date
}

const RecentLeadsTable: React.FC = () => {
  // Mock data
  const leads: Lead[] = [
    {
      id: '1',
      fullName: 'John Smith',
      phone: '+1555123456',
      status: 'qualified',
      source: 'WhatsApp',
      qualificationScore: 85,
      createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
    },
    {
      id: '2',
      fullName: 'Maria Garcia',
      phone: '+1555234567',
      status: 'new',
      source: 'Facebook Ads',
      qualificationScore: 65,
      createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
    },
    {
      id: '3',
      fullName: 'David Johnson',
      phone: '+1555345678',
      status: 'contacted',
      source: 'Instagram Ads',
      createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000), // 6 hours ago
    },
    {
      id: '4',
      fullName: 'Sarah Wilson',
      phone: '+1555456789',
      status: 'booking_requested',
      source: 'Google Ads',
      qualificationScore: 92,
      createdAt: new Date(Date.now() - 12 * 60 * 60 * 1000), // 12 hours ago
    },
    {
      id: '5',
      fullName: 'Michael Brown',
      phone: '+1555567890',
      status: 'qualified',
      source: 'Referral',
      qualificationScore: 78,
      createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000), // 1 day ago
    },
  ]

  const getStatusColor = (status: string) => {
    const colors = {
      new: 'primary',
      qualified: 'success',
      contacted: 'warning',
      booking_requested: 'secondary',
    }
    return colors[status as keyof typeof colors] || 'default'
  }

  const getStatusLabel = (status: string) => {
    const labels = {
      new: 'New',
      qualified: 'Qualified',
      contacted: 'Contacted',
      booking_requested: 'Booking Req.',
    }
    return labels[status as keyof typeof labels] || status
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h3" fontWeight="bold">
            Recent Leads
          </Typography>
          <Link href="/leads" color="primary" underline="hover">
            View all
          </Link>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Lead</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leads.map((lead) => (
                <TableRow key={lead.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                        {lead.fullName.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {lead.fullName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {lead.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(lead.status)}
                      color={getStatusColor(lead.status) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {lead.source}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {lead.qualificationScore ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 80 }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 24 }}>
                          {lead.qualificationScore}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={lead.qualificationScore}
                          sx={{
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 2,
                            },
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Not scored
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatTime(lead.createdAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}

export default RecentLeadsTable
